// The queues of the Prospects pages and what each shows; pure, so links and
// decisions can be reasoned about without the page.

import type { ProspectFilters } from "../../api/client.ts";
import type { ReviewDecision } from "../../components/review/types.ts";

export type Queue = "inbox" | "worklist" | "done";

/** What a decision from a row or the drawer was. */
export type QueueDecision = Pick<ReviewDecision, "action" | "snooze"> | { action: "reset" | "done" | "reopen" };

// One "Show" filter on the inbox instead of one per state: a reviewer looks at
// one queue at a time. The inbox opens on what needs a decision: a step
// waiting to continue, an undecided company, or one a new hit reopened.
export const SHOW_OPTIONS: { value: string; label: string; filters: Partial<ProspectFilters> }[] = [
  // The default, so it never appears in the URL.
  { value: "inbox", label: "Needs a decision", filters: { queue: "inbox" } },
  { value: "awaiting", label: "Waiting to continue", filters: { awaiting_approval: true } },
  { value: "review", label: "To review", filters: { review_state: ["pending"] } },
  { value: "disqualified", label: "Disqualified", filters: { review_state: ["disqualified"] } },
  { value: "all", label: "All prospects", filters: {} },
];
// Links made for the signals inbox's queues.
export const SHOW_ALIASES: Record<string, string> = { used: "pursuing", pursuing: "all" };

export function showOption(value: string | null) {
  const canonical = SHOW_ALIASES[value ?? ""] ?? value ?? "";
  return SHOW_OPTIONS.find((o) => o.value === canonical) ?? SHOW_OPTIONS[0];
}

/** The API filters a queue (and the inbox's "Show") stands for. */
export function queueFilters(
  queue: Queue,
  show: string | null,
  snoozedOnly: boolean,
): Partial<ProspectFilters> {
  if (queue === "worklist") return { queue: "worklist", ...(snoozedOnly && { snoozed: true }) };
  if (queue === "done") return { queue: "done" };
  return showOption(show).filters;
}

/** Whether a decision takes the row out of what the view shows. */
export function leavesView(
  queue: Queue,
  show: string | null,
  snoozedOnly: boolean,
  decision: QueueDecision,
): boolean {
  if (queue === "worklist") {
    if (decision.action === "disqualify" || decision.action === "reset" || decision.action === "done") {
      return true;
    }
    return decision.action === "accept" && !!decision.snooze && !snoozedOnly;
  }
  if (queue === "done") return decision.action === "reopen";
  switch (showOption(show).value) {
    case "inbox":
    case "awaiting":
    case "review":
      return decision.action === "accept" || decision.action === "disqualify";
    case "disqualified":
      return decision.action === "accept" || decision.action === "reset";
    default:
      return false;
  }
}
